/**
 * Pantalla de metricas: tarjetas con los valores de `MetricsResponse`.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { MetricsResponse } from '@shared/api-types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { formatDuration, formatNumber, formatPercent } from '../format.ts';
import { ErrorBanner, SkeletonBlock, StatCard } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; metrics: MetricsResponse }
  | { status: 'error'; message: string };

export function MetricsScreen(): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .getMetrics(controller.signal)
      .then((metrics) => setState({ status: 'ready', metrics }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Metricas de atencion</h1>
          <p className="page__subtitle">
            Resumen del estado de la operacion. Los datos se calculan sobre la organizacion activa.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <Icon name="refresh" />
          Actualizar
        </button>
      </header>

      {state.status === 'loading' ? (
        <div className="stats">
          <SkeletonBlock height={92} />
          <SkeletonBlock height={92} />
          <SkeletonBlock height={92} />
          <SkeletonBlock height={92} />
          <SkeletonBlock height={92} />
          <SkeletonBlock height={92} />
        </div>
      ) : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' ? (
        <div className="stats">
          <StatCard
            label="Conversaciones abiertas"
            value={formatNumber(state.metrics.conversationsOpen)}
            hint="En estado abierta, sin resolver"
          />
          <StatCard
            label="Pendientes"
            value={formatNumber(state.metrics.conversationsPending)}
            hint="Esperando respuesta del cliente"
          />
          <StatCard
            label="Mensajes sin leer"
            value={formatNumber(state.metrics.unreadTotal)}
            hint="Suma de todos los no leidos"
          />
          <StatCard
            label="Mensajes recibidos hoy"
            value={formatNumber(state.metrics.messagesInboundToday)}
            hint="Mensajes del cliente"
          />
          <StatCard
            label="Mensajes enviados hoy"
            value={formatNumber(state.metrics.messagesOutboundToday)}
            hint="Agentes y asistente de IA"
          />
          <StatCard
            label="Tasa de automatizacion"
            value={formatPercent(state.metrics.automationRate)}
            hint="Conversaciones resueltas sin intervencion humana (7 dias)"
          />
          <StatCard
            label="Escalados a personas"
            value={formatNumber(state.metrics.handoffsLast7Days)}
            hint="Ultimos 7 dias"
          />
          <StatCard
            label="Primera respuesta (media)"
            value={formatDuration(state.metrics.averageFirstResponseSeconds)}
            hint={
              state.metrics.averageFirstResponseSeconds === null
                ? 'Todavia no hay datos suficientes'
                : 'Tiempo medio hasta la primera respuesta'
            }
          />
          <StatCard
            label="Ejecuciones de la IA"
            value={formatNumber(state.metrics.aiRunsLast7Days)}
            hint="Ultimos 7 dias"
          />
        </div>
      ) : null}
    </div>
  );
}
